import { useEffect } from "react";

interface MovieModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  meta: { label: string; value: string }[];
  blurb: string;
  tags: string[];
  quality?: string;
  poster?: string;
}

export default function MovieModal({
  open,
  onClose,
  title,
  meta,
  blurb,
  tags,
  quality,
  poster,
}: MovieModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-0 md:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={`${title} 详情`}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute inset-0 bg-foreground-950/70 backdrop-blur-sm cursor-pointer animate-soft-fade"
        aria-label="关闭详情"
      ></button>

      <div className="relative w-full md:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-lg md:rounded-lg bg-background-50 border border-background-200 animate-modal-in">
        <div className="flex items-start gap-4 md:gap-6 p-5 md:p-6">
          {poster && (
            <div className="hidden sm:block w-32 md:w-40 shrink-0 aspect-[2/3] rounded-md overflow-hidden bg-background-200">
              <img src={poster} alt={`${title} 海报`} className="w-full h-full object-cover object-top" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{title}</h3>
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {quality && (
              <span className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-medium">
                <i className="ri-hd-line"></i>
                {quality}
              </span>
            )}

            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2">
              {meta.map((item) => (
                <div key={item.label} className="min-w-0">
                  <dt className="text-xs text-foreground-500">{item.label}</dt>
                  <dd className="text-sm text-foreground-900 truncate">{item.value}</dd>
                </div>
              ))}
            </dl>

            <p className="mt-4 text-sm text-foreground-700 leading-relaxed">{blurb}</p>

            <div className="mt-4 flex flex-wrap gap-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-fill"></i>
                立即播放
              </button>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-100 border border-background-300 text-foreground-800 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-bookmark-line"></i>
                加入收藏
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}